import React, {useEffect, useRef} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {
  Button,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  FormHelperText,
  FormControl,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {IGenericType, IFdOperatingCost} from 'constants/interfaces';
import {ToggleButton, ToggleButtonGroup} from '@material-ui/lab';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
const QUARTERS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
type Frequency = 'once-off' | 'monthly' | 'quarterly';

export type Submission = {
  id?: number;
  expenseId: number;
  total: number;
  year: number;
  frequency: Frequency;
  months: string[];
  quarters: Quarter[];
  notes?: string;
};

const selectedButtonBg = '#acecce';
const selectedButtonColor = '#013333';

export function FdOperatingCostForm(props: {
  operatingCost?: IFdOperatingCost;
  allExpenses: IGenericType[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (operatingCost: Submission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  readonly?: boolean;
}) {
  const isFirstRender = useRef(true);

  const monthlyCosts = props.operatingCost
    ? JSON.parse(props.operatingCost.monthlyCosts)
    : {};

  const {
    handleSubmit,
    control,
    setValue,
    formState: {errors},
    clearErrors,
  } = useForm<Submission>({
    defaultValues: {
      id: props.operatingCost?.id
        ? parseInt(props.operatingCost.id)
        : undefined,
      year: 2025,
      expenseId: props.operatingCost?.expense?.id,
      frequency: monthlyCosts.frequency || 'once-off',
      total: monthlyCosts.total,
      months:
        monthlyCosts.months || monthlyCosts.month ? [monthlyCosts.month] : [],
      quarters: monthlyCosts.quarters,
      notes: props.operatingCost?.notes || '',
    },
  });

  const frequency = useWatch({control, name: 'frequency'});

  /* ─── reset month/quarter selections if frequency changes ─── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setValue('months', []);
    setValue('quarters', []);
    clearErrors(['months', 'quarters']);
  }, [frequency, setValue, clearErrors]);

  const onSubmit = (submission: Submission) => props.onSubmit(submission);

  /* ─── helpers for conditional validation ─── */
  const getMonthsRequired = () =>
    frequency === 'once-off' || frequency === 'monthly';
  const getQuartersRequired = () => frequency === 'quarterly';

  return (
    <div className="generic-form-container operating-cost-form">
      <div className="form-container" style={{width: '100%'}}>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                General Information
              </Typography>
            </Grid>

            {/* Expense selector */}
            <Grid item xs={12}>
              <Controller
                name="expenseId"
                control={control}
                rules={{required: 'Expense is required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Autocomplete
                    options={props.allExpenses}
                    getOptionLabel={(o: IGenericType) => o?.name || ''}
                    value={
                      props.allExpenses.find((e) => e.id === value) || null
                    }
                    onChange={(_, newVal) => onChange(newVal?.id)}
                    disabled={props.readonly || props.isLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Expense"
                        variant="outlined"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Total */}
            <Grid item xs={12}>
              <Controller
                name="total"
                control={control}
                rules={{required: 'Expenses Total required'}}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Expenses Total (Rands)"
                    type="number"
                    variant="outlined"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    fullWidth
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Frequency */}
            <Grid item xs={12}>
              <Controller
                name="frequency"
                control={control}
                rules={{required: 'Frequency is required'}}
                render={({field: {onChange, value}}) => (
                  <FormControl component="fieldset" error={!!errors.frequency}>
                    <Typography variant="subtitle2" gutterBottom>
                      Frequency
                    </Typography>
                    <ToggleButtonGroup
                      color="primary"
                      size="small"
                      value={value}
                      exclusive
                      onChange={(_, newVal) => newVal && onChange(newVal)}
                    >
                      {(
                        ['once-off', 'monthly', 'quarterly'] as Frequency[]
                      ).map((f) => (
                        <ToggleButton
                          key={f}
                          value={f}
                          aria-label={f}
                          style={{
                            backgroundColor:
                              value === f ? selectedButtonBg : 'inherit',
                            color:
                              value === f ? selectedButtonColor : 'inherit',
                          }}
                        >
                          {f === 'once-off'
                            ? 'Once-off'
                            : f.charAt(0).toUpperCase() + f.slice(1)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {errors.frequency && (
                      <FormHelperText>
                        {errors.frequency.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Conditional selectors for months/quarters */}
            {frequency === 'once-off' && (
              <Grid item xs={12}>
                <Controller
                  name="months"
                  control={control}
                  rules={{
                    validate: (v) =>
                      getMonthsRequired() && (!v || v.length !== 1)
                        ? 'Please select one month'
                        : true,
                  }}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      options={MONTHS}
                      getOptionLabel={(o) => o}
                      value={value[0] || null}
                      onChange={(_, newVal) => onChange(newVal ? [newVal] : [])}
                      disabled={props.readonly || props.isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Month"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={
                            error ? error.message : 'Select the month'
                          }
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {frequency === 'monthly' && (
              <Grid item xs={12}>
                <Controller
                  name="months"
                  control={control}
                  rules={{
                    validate: (v) =>
                      getMonthsRequired() && (!v || v.length === 0)
                        ? 'Please select at least one month'
                        : true,
                  }}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      multiple
                      options={MONTHS}
                      getOptionLabel={(o) => o}
                      value={value}
                      onChange={(_, newVal) => onChange(newVal)}
                      disabled={props.readonly || props.isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Months"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : 'Select all months this expense applies to'
                          }
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {frequency === 'quarterly' && (
              <Grid item xs={12}>
                <Controller
                  name="quarters"
                  control={control}
                  rules={{
                    validate: (v) =>
                      getQuartersRequired() && (!v || v.length === 0)
                        ? 'Please select at least one quarter'
                        : true,
                  }}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      multiple
                      options={QUARTERS}
                      getOptionLabel={(o) => o}
                      value={value || []}
                      onChange={(_, newVal) => onChange(newVal)}
                      disabled={props.readonly || props.isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Quarters"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : 'Select all quarters this expense applies to'
                          }
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    rowsMax={6}
                    value={value || ''}
                    onChange={onChange}
                    placeholder="Enter any additional notes…"
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Action buttons */}
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
